import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import type {
  APIColumnDataSource,
  APIColumnDataSourceConfig,
  APITable,
  ColumnDataSourceKeyValueDataType,
  RelationColumnDataSourceConfig,
} from "../../../../../../../api/infrastructure/table.formatter";
import type { TableColumnTempInput } from "../../../../../../../api/infrastructure/table.transformer";
import { TABLE_COLUMN_TYPES } from "../../../../../../../constants";
import { useSidebar } from "../../../../../../providers/SidebarProvider";
import { transformToValidTableColumnName } from "../../../../../../shared/add-table-column";
import { showToast } from "../../../../../../shared/global-tools/toast-system";
import OurDropdown from "../../../../../utils/OurDropdown/OurDropdown";
import OurSwitchInput from "../../../../../utils/OurSwitchInput/OurSwitchInput";
import "./AddTableColumn.scss";
import RelationTableColumnForm from "./RelationTableColumnForm";
import TableColumnAPIForm from "./TableColumnAPIForm/TableColumnAPIForm";
import TableColumnEnumForm from "./TableColumnEnumForm/TableColumnEnumForm";

interface AddTableColumnProps {
  currentTable: APITable;
  tables: APITable[];
  onChange(val: TableColumnTempInput): void;
  tableColumnInputs: TableColumnTempInput[];
}

export default function AddTableColumn({
  currentTable,
  onChange,
  tableColumnInputs,
  tables,
}: AddTableColumnProps) {
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  // const [length, setLength] = useState<number>();
  const [type, setType] = useState<string>("string");
  const [required, setRequired] = useState(false);
  const [unique, setUnique] = useState(false);
  const [keyValues, setKeyValues] = useState<ColumnDataSourceKeyValueDataType[]>();
  const [apiConfig, setApiConfig] = useState<APIColumnDataSourceConfig>();
  const [relationConfig, setRelationConfig] = useState<RelationColumnDataSourceConfig>();
  const { openSidebar, closeSidebar } = useSidebar();

  const handleAddColumn = () => {
    if (name === "") {
      showToast("Le nom du champ est requis", {
        severity: "warn",
      });
      return;
    }

    let validColumnName = transformToValidTableColumnName(name);
    const isNameTaken = tableColumnInputs.some((col) => col.name === validColumnName);
    if (isNameTaken) {
      showToast("Le libellé du champ est déjà utilisé", {
        severity: "warn",
      });
      return;
    }

    let dataSource: APIColumnDataSource | undefined;
    if (type === "enum-key-value") {
      if (!keyValues || keyValues.length === 0) {
        showToast("Les valeurs clés sont requises", {
          severity: "warn",
        });
        return;
      }
      dataSource = {
        type: "key_value",
        data: keyValues,
      };
    } else if (type === "enum-api") {
      if (!apiConfig) {
        showToast("La configuration de l'API est requise", {
          severity: "warn",
        });
        return;
      }
      dataSource = {
        type: "api",
        config: apiConfig,
      };
    } else if (type === "enum-relation") {
      if (!relationConfig) {
        showToast("La configuration de la relation est requise", {
          severity: "warn",
        });
        return;
      }
      validColumnName = `${validColumnName}_id`;
      dataSource = {
        type: "relation",
        config: relationConfig,
      };
    }

    onChange({
      label,
      name: validColumnName,
      type,
      // length,
      required,
      unique,
      nullable: true,
      dataSource,
    });
    setName("");
    setLabel("");
    setRequired(false);
    setUnique(false);
    // setLength(undefined);
    setType("string");
    setKeyValues(undefined);
    setApiConfig(undefined);
  };

  const onColumnTypeChange = (val: string) => {
    if (val === "enum-key-value") {
      openSidebar({
        modal: true,
        body: (
          <TableColumnEnumForm
            initialKeyValues={keyValues}
            close={closeSidebar}
            onChange={setKeyValues}
          />
        ),
        position: "right",
      });
    }
    if (val === "enum-api") {
      openSidebar({
        modal: true,
        body: (
          <TableColumnAPIForm close={closeSidebar} apiConfig={apiConfig} onChange={setApiConfig} />
        ),
        position: "right",
      });
    }
    if (val === "enum-relation") {
      if (tables.length <= 1) {
        showToast("Au moins deux tables sont requises pour une relation", {
          severity: "warn",
        });
        return;
      }
      openSidebar({
        modal: true,
        body: (
          <RelationTableColumnForm
            close={closeSidebar}
            currentTable={currentTable}
            tables={tables}
            relationConfig={relationConfig}
            onChange={setRelationConfig}
            onCancel={() => {
              setRelationConfig(undefined);
              setType("string");
            }}
          />
        ),
        position: "right",
      });
    }
    setType(val);
  };

  return (
    <div className="AddTableColumn">
      <h4 className="AddTableColumn-title">Ajouter un nouveau champ</h4>

      <form
        className="AddTableColumn-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleAddColumn();
        }}
      >
        <div className="AddTableColumn-formGroupList">
          <label className="Field">
            {/* TODO: standardize styles */}
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              Libellé du champ
            </span>
            <InputText
              className="Field-input small AddTableColumn-input"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
              }}
              required
            />
          </label>

          <label className="Field">
            {/* TODO: standardize styles */}
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              Nom du champ
            </span>
            <InputText
              className="Field-input small AddTableColumn-input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              required
            />
          </label>

          <label className="Field">
            {/* TODO: standardize styles */}
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              Type du champ
            </span>
            <OurDropdown<string>
              initialOptions={TABLE_COLUMN_TYPES}
              className="sm AddTableColumn-dropdown"
              selectedOption={type}
              alwaysTriggerOnChange={true}
              onChange={onColumnTypeChange}
            />
          </label>

          {/* TODO: standardize styles */}
          <label className="Field">
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              Est obligatoire?
            </span>
            <div style={{ marginTop: "22px" }}>
              <OurSwitchInput value={required} onChange={setRequired} />
            </div>
          </label>

          <label className="Field">
            <span className="Field-label" style={{ fontWeight: 500, fontSize: "13px" }}>
              Unique
            </span>
            <div style={{ marginTop: "22px" }}>
              <OurSwitchInput value={unique} onChange={setUnique} />
            </div>
          </label>
        </div>

        <div style={{ marginBottom: "5px" }}>
          <Button type="submit" label="Ajouter" className="AddTableColumn-btn Btn primary" />
        </div>
      </form>
    </div>
  );
}
